/**
 * The batch ledger. A full page, not a drawer, because it is a reconciliation surface.
 *
 * This is what every WAC cell in the product links to, and it is the only place a cost is
 * shown that is not in the ledger. Four things have to be true on it at once:
 *   1. the running WAC after each event, so the number can be traced
 *   2. the FIFO remaining per batch, so the lot on the shelf can be matched to a COA
 *   3. cost revisions shown as rows, not as edits, so a restated month is visible
 *   4. the supplier cost trend and the live quotes beside it, so a reorder decision is made
 *      here rather than across three tabs
 */
import { Card, EmptyState, KpiTile, MoneyText, Pill, Skeleton } from '@/components';
import { formatCount, formatDaysLeft, formatPercent, formatVelocity, relativeAge } from '@/lib/format';
import type { MetricEnvelope } from '@elutide/shared';
import { CostBySupplierChart } from './CostBySupplierChart';
import { useBatches, useCompare, useHistory, useLedger, useStockDetail } from './queries';
import './stock.css';

const EVENT_LABEL: Record<string, string> = {
  opening_balance: 'Opening balance',
  receipt: 'Receipt',
  sale: 'Sale',
  restock: 'Restock',
  adjustment: 'Adjustment',
  cost_revision: 'Cost revision',
};

function point(metricId: string, value: number | null, unit: MetricEnvelope['unit']): MetricEnvelope {
  return {
    metric_id: metricId,
    version: 1,
    value,
    unit,
    basis: 'ledger',
    window: { kind: 'point', start: null, end: null, tz: 'America/New_York', label: 'now' },
    as_of: new Date().toISOString(),
    sources: ['wac_ledger'],
    dims: null,
    partial: false,
    coverage: null,
  };
}

export function BatchLedgerPage({ productId }: { productId: number }) {
  const detail = useStockDetail(productId);
  const ledger = useLedger(productId);
  const batches = useBatches(productId);
  const history = useHistory(productId, '90d');
  const compare = useCompare(productId);

  const row = detail.data;
  const last = ledger.data?.last_receipt ?? null;

  if (detail.isLoading) {
    return <Skeleton shape="card" />;
  }

  if (!row) {
    return <EmptyState message="That product does not exist, or you cannot see it." />;
  }

  return (
    <div className="stock-page">
      <header className="stock-ledger__head">
        <h1 className="stock-ledger__title">
          {row.name} {row.size_label ? <span className="o-muted">{row.size_label}</span> : null}
        </h1>
        <span className="o-mono o-muted">{row.sku ?? 'no SKU'}</span>
        {row.is_visible ? null : <Pill tone="neutral">hidden, not restocking</Pill>}
      </header>

      <div className="o-kpis stock-kpis">
        <KpiTile
          label="On hand"
          envelope={point('stock_on_hand', row.available_qty, 'count')}
          note={`shelf ${formatCount(row.shelf_qty)} including ${formatCount(row.committed_qty)} committed`}
        />
        <KpiTile
          label="Running WAC"
          envelope={point('wac', row.wac_cents, 'cents')}
          note={
            row.wac_source === 'estimate'
              ? 'Estimated from a supplier quote, no batches yet'
              : row.last_received_date
                ? `last receipt ${row.last_received_date}`
                : null
          }
          showCents
        />
        <KpiTile
          label="Inventory at cost"
          envelope={point(
            'inventory_at_cost',
            row.wac_cents === null ? null : Math.max(row.available_qty, 0) * row.wac_cents,
            'cents',
          )}
          note={`${row.batch_count} batches all time`}
        />
        <KpiTile
          label="30d velocity"
          envelope={point('velocity_30d', row.velocity_30d, 'units_per_day')}
          note={`${formatCount(row.units_30d)} units in the window`}
        />
        <KpiTile
          label="Days left"
          envelope={point('days_left', row.days_left, 'days')}
          note={row.inbound_qty > 0 ? `${row.inbound_qty} inbound${row.inbound_eta ? `, ${row.inbound_eta}` : ''}` : null}
        />
        <KpiTile
          label="Price and gross margin"
          envelope={point('price', row.price_cents, 'cents')}
          note={
            row.gross_margin_pct === null
              ? 'No cost, so no margin'
              : `${formatPercent(row.gross_margin_pct)} at the current WAC`
          }
        />
      </div>

      {last ? (
        <Card title="Why the WAC is this number" basis="ledger">
          <p className="stock-why">
            Before the last receipt: {formatCount(last.on_hand_before)} units at{' '}
            <MoneyText cents={last.wac_before_cents} showCents /> each. Received{' '}
            {formatCount(last.qty_received)} at <MoneyText cents={last.unit_cost_cents} showCents />{' '}
            landed. New weighted average{' '}
            <strong>
              <MoneyText cents={last.wac_after_cents} showCents />
            </strong>
            .
          </p>
          {ledger.data && ledger.data.warnings.length > 0 ? (
            <ul className="stock-warnings">
              {ledger.data.warnings.map((warning) => (
                <li key={warning}>{warning}</li>
              ))}
            </ul>
          ) : null}
        </Card>
      ) : null}

      <div className="stock-ledger__grid">
        <Card title="Batch ledger" basis="ledger">
          {batches.isLoading ? (
            <Skeleton shape="row" lines={5} />
          ) : (batches.data?.items.length ?? 0) === 0 ? (
            <EmptyState message="No batches yet. This SKU is valued from its opening balance or a supplier quote." />
          ) : (
            <div className="stock-scroll" data-allow-hscroll>
              <table className="stock-table">
                <caption className="o-visually-hidden">Batches received for this SKU</caption>
                <thead>
                  <tr>
                    <th scope="col">Batch and PO</th>
                    <th scope="col">Supplier</th>
                    <th scope="col">Received</th>
                    <th scope="col" className="stock-num">Qty</th>
                    <th scope="col" className="stock-num">Goods</th>
                    <th scope="col" className="stock-num">Freight</th>
                    <th scope="col" className="stock-num">Testing</th>
                    <th scope="col" className="stock-num">Landed</th>
                    <th scope="col" className="stock-num">Left</th>
                    <th scope="col">COA</th>
                    <th scope="col" className="stock-num">WAC after</th>
                  </tr>
                </thead>
                <tbody>
                  {(batches.data?.items ?? []).map((batch) => (
                    <tr key={batch.batch_id}>
                      <td>
                        <span className="o-mono">{batch.lot_number ?? batch.batch_id.slice(-6)}</span>
                        <br />
                        <span className="o-mono o-muted">{batch.po_number ?? 'no PO'}</span>
                      </td>
                      <td>{batch.supplier_name ?? 'unknown'}</td>
                      <td>{batch.received_at ?? 'not received'}</td>
                      <td className="stock-num">{formatCount(batch.qty_received)}</td>
                      <td className="stock-num">
                        <MoneyText cents={batch.unit_goods_cents} showCents />
                      </td>
                      <td className="stock-num">
                        <MoneyText cents={batch.freight_alloc_cents} showCents />
                      </td>
                      <td className="stock-num">
                        <MoneyText cents={batch.testing_alloc_cents} showCents />
                      </td>
                      <td className="stock-num">
                        <MoneyText cents={batch.landed_unit_cents} showCents />
                        {batch.cost_status === 'provisional' ? (
                          <>
                            {' '}
                            <Pill tone="warn">provisional</Pill>
                          </>
                        ) : null}
                      </td>
                      <td className="stock-num">{formatCount(batch.fifo_remaining)}</td>
                      <td>
                        {batch.coa_present ? <Pill tone="ok">yes</Pill> : <Pill tone="warn">missing</Pill>}
                      </td>
                      <td className="stock-num">
                        {batch.wac_after_cents === null ? (
                          <span className="o-muted">n/a</span>
                        ) : (
                          <MoneyText cents={batch.wac_after_cents} showCents />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        <div className="stock-ledger__side">
          <Card title="Cost per unit by supplier" basis="ledger">
            {history.isLoading ? (
              <Skeleton shape="card" />
            ) : (
              <CostBySupplierChart receipts={history.data?.receipts ?? []} />
            )}
          </Card>

          <Card title="Source pricing" basis="manual">
            {compare.isLoading ? (
              <Skeleton shape="row" lines={3} />
            ) : (compare.data?.rows.length ?? 0) === 0 ? (
              <EmptyState message="No supplier quotes recorded for this SKU." />
            ) : (
              <table className="stock-table">
                <caption className="o-visually-hidden">Supplier quotes for this SKU</caption>
                <thead>
                  <tr>
                    <th scope="col">Supplier</th>
                    <th scope="col" className="stock-num">Landed est.</th>
                    <th scope="col" className="stock-num">vs WAC</th>
                    <th scope="col">Lead p80</th>
                  </tr>
                </thead>
                <tbody>
                  {(compare.data?.rows ?? []).map((quote) => (
                    <tr key={quote.supplier_id}>
                      <td>
                        {quote.supplier}
                        {quote.stale ? (
                          <>
                            {' '}
                            <Pill tone="warn">stale</Pill>
                          </>
                        ) : null}
                      </td>
                      <td className="stock-num">
                        <MoneyText cents={quote.landed_estimate_cents} showCents />
                      </td>
                      <td className="stock-num">
                        {quote.vs_wac_pct === null ? (
                          <span className="o-muted">n/a</span>
                        ) : (
                          <span className={quote.vs_wac_pct < 0 ? 'stock-better' : ''}>
                            {formatPercent(quote.vs_wac_pct)}
                          </span>
                        )}
                      </td>
                      <td>{quote.lead_p80_days === null ? 'n/a' : `${Math.round(quote.lead_p80_days)}d`}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          <Card title="Sales and adjustments" basis="woo_paid">
            {history.isLoading ? (
              <Skeleton shape="row" lines={3} />
            ) : (
              <>
                <p>
                  {formatCount(history.data?.sales.reduce((sum, s) => sum + s.qty, 0) ?? 0)} units
                  across {history.data?.sales.length ?? 0} orders in 90 days.{' '}
                  {history.data?.sales[0]
                    ? `Last sale ${relativeAge(history.data.sales[0].date_paid)}.`
                    : 'No sales in the window.'}
                </p>
                <p className="o-muted">
                  Velocity {formatVelocity(row.velocity_30d)} per day, cover{' '}
                  {formatDaysLeft(row.days_left)}.
                </p>
                <ul className="stock-adjustments">
                  {(history.data?.adjustments ?? []).slice(0, 8).map((adjustment) => (
                    <li key={adjustment.adjustment_id}>
                      <span className={adjustment.delta < 0 ? 'stock-out' : ''}>
                        {adjustment.delta > 0 ? `+${adjustment.delta}` : adjustment.delta}
                      </span>{' '}
                      {adjustment.reason.replace(/_/g, ' ')}
                      {adjustment.approval_state === 'pending_approval' ? (
                        <>
                          {' '}
                          <Pill tone="warn">pending approval</Pill>
                        </>
                      ) : null}{' '}
                      <span className="o-muted">{relativeAge(adjustment.created_at)}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </Card>
        </div>
      </div>

      <Card title="Ledger events" basis="ledger">
        {ledger.isLoading ? (
          <Skeleton shape="row" lines={6} />
        ) : (ledger.data?.items.length ?? 0) === 0 ? (
          <EmptyState message="This SKU has no ledger entries. It has no cost until one is entered." />
        ) : (
          <div className="stock-scroll" data-allow-hscroll>
            <table className="stock-table">
              <caption className="o-visually-hidden">Weighted average cost ledger</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Event</th>
                  <th scope="col">Reference</th>
                  <th scope="col" className="stock-num">Qty</th>
                  <th scope="col" className="stock-num">Unit cost</th>
                  <th scope="col" className="stock-num">On hand after</th>
                  <th scope="col" className="stock-num">WAC after</th>
                </tr>
              </thead>
              <tbody>
                {(ledger.data?.items ?? []).map((entry) => (
                  <tr
                    key={entry.entry_id}
                    className={entry.event_type === 'cost_revision' ? 'stock-revision' : ''}
                  >
                    <td>{entry.event_date}</td>
                    <td>{EVENT_LABEL[entry.event_type] ?? entry.event_type}</td>
                    <td>
                      <span className="o-mono o-muted">{entry.event_ref ?? ''}</span>
                      {entry.reason ? <div className="o-muted">{entry.reason}</div> : null}
                    </td>
                    <td className="stock-num">
                      {entry.qty_delta > 0 ? `+${entry.qty_delta}` : entry.qty_delta}
                    </td>
                    <td className="stock-num">
                      {entry.unit_cost_cents === null ? (
                        <span className="o-muted">n/a</span>
                      ) : (
                        <MoneyText cents={entry.unit_cost_cents} showCents />
                      )}
                    </td>
                    <td className="stock-num">{formatCount(entry.on_hand_after)}</td>
                    <td className="stock-num">
                      <MoneyText cents={entry.wac_after_cents} showCents />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
